import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION,
    UPDATE_STUDY_HUB_MONITORING_TOOLKIT_CONFIG,
} from '@queries/central-monitoring';
import {
    getMonitoringToolkitConfig,
    getMonitoringToolkitConfig_OpraQueries_getMonitoringToolkitConfig_StudyHubMonitoringToolkitConfig as StudyHubMonitoringToolkitConfig,
    getMonitoringToolkitConfigVariables,
    updateMonitoringToolkitConfig,
    updateMonitoringToolkitConfigVariables,
} from '@queries/generated/central-monitoring.generated';

const COMPONENT_NAME = 'monitoringToolkit';

const MonitoringToolkitConfig = ({ studyRef, canEdit }: { studyRef: string | null; canEdit: boolean }) => {
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges } = useUnsavedChangesContext();

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('admin');
    const isEditMode = useBoolean(false);

    const [loadConfig, { data: configData, loading: configLoading, error: dataError, refetch }] = useLazyQuery<
        getMonitoringToolkitConfig,
        getMonitoringToolkitConfigVariables
    >(GET_STUDY_HUB_MONITORING_TOOLKIT_CONFIGURATION);

    const [saveConfig, { loading: isMutatingStudyConfigData, error: mutationError }] = useMutation<
        updateMonitoringToolkitConfig,
        updateMonitoringToolkitConfigVariables
    >(UPDATE_STUDY_HUB_MONITORING_TOOLKIT_CONFIG);

    const defaultValues = { allowKRIReviews: configData?.getMonitoringToolkitConfig?.allowKRIReviews };

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = useForm<StudyHubMonitoringToolkitConfig>({ mode: 'all', defaultValues });

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, [studyRef]);

    useEffect(() => {
        if (configData) {
            const allowKRIReviews = configData.getMonitoringToolkitConfig?.allowKRIReviews;
            reset({ allowKRIReviews }, { keepDirty: false });
        }
    }, [configData]);

    const onSubmit = handleSubmit(async ({ allowKRIReviews }) => {
        if (isDirty) {
            saveConfig({
                variables: {
                    studyId: studyRef,
                    monitoringToolkitConfig: {
                        id: configData?.getMonitoringToolkitConfig?.id as string,
                        allowKRIReviews: allowKRIReviews ?? false,
                    },
                },
            })
                .then(async () => {
                    isEditMode.setFalse();
                    ShowSuccess(tRoot('actionsSuccessfulToast'));
                    await refetch();
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            isEditMode.setFalse();
        }
    });

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, []);

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
    }, [isEditMode.value]);

    const isLoading = !configData || configLoading || isMutatingStudyConfigData;
    const isError = dataError || mutationError;

    return (
        <EditCard
            title={t('monitoringToolkitConfig')}
            onEdit={() => isEditMode.setTrue()}
            onCancel={async () => {
                if (componentHasChanges(COMPONENT_NAME)) {
                    const ok = await confirmDiscardChanges('confirmation-dialog');
                    if (!ok) {
                        return;
                    }
                }
                reset(defaultValues, { keepDirty: false });
                isEditMode.setFalse();
            }}
            onSave={onSubmit}
            isEditMode={isEditMode.value}
            disabled={!canEdit || isLoading}
            data-testid="panels-config"
        >
            {isLoading && !isError && <Loading />}
            {isError && <ErrorMessage />}
            <div className="k-display-flex k-flex-nowrap k-align-items-center">
                <div className="k-display-flex k-flex-nowrap k-align-items-center" style={{ minWidth: 425 }}>
                    <Controller
                        control={control}
                        name="allowKRIReviews"
                        render={({ field: { onChange, value } }) => (
                            <CoreCheckbox
                                name="allowKRIReviews"
                                value={value}
                                onChange={onChange}
                                disabled={!isEditMode.value}
                                label={t('study:allowKRIReviews')}
                                data-testid="allow-kri-reviews-form"
                            />
                        )}
                    />
                </div>
            </div>
        </EditCard>
    );
};

export default MonitoringToolkitConfig;
